import React from "react";
import { DOTS, usePagination } from "@/hooks/usePagination";
import { useSessionStorage } from "@/hooks/useSessionStorage";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { IoIosArrowDown } from "react-icons/io";

export const CustomPagination = ({
  onPageChange,
  totalCount = 1,
  siblingCount = 1,
  currentPage = 1,
  pageSize,
  persistPagination = false,
  persistenceKey = "",
}: {
  onPageChange: (value: number) => void;
  totalCount?: number;
  pageSize: number;
  siblingCount?: number;
  currentPage?: number;
  persistPagination?: boolean;
  persistenceKey?: string;
}) => {
  const paginationRange = usePagination({
    currentPage,
    totalCount,
    pageSize,
    siblingCount,
  });

  const [storedPage, setStoredPage] = useSessionStorage({
    key: `${persistenceKey}_page`,
    initialValue: currentPage,
    onError: (error: Error) => {
      console.error("Failed to handle pagination storage:", error);
    },
  });

  React.useEffect(() => {
    if (persistPagination && persistenceKey && storedPage !== currentPage) {
      onPageChange(storedPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistPagination, persistenceKey, storedPage, currentPage]);

  const handlePageChange = (value: number) => {
    onPageChange(value);
    if (persistPagination && persistenceKey) {
      setStoredPage(value);
    }
  };

  if (currentPage === 0 || paginationRange.length < 2) {
    return null;
  }

  const onNext = () => {
    handlePageChange(currentPage + 1);
  };

  const onPrevious = () => {
    handlePageChange(currentPage - 1);
  };

  const lastPage = paginationRange[paginationRange.length - 1];
  return (
    <Pagination>
      <PaginationContent className="flex-wrap">
        {currentPage !== 1 && (
          <PaginationItem>
            <PaginationPrevious onClick={onPrevious} />
          </PaginationItem>
        )}

        {paginationRange.map((pageNumber, index) => {
          if (pageNumber === DOTS) {
            return (
              <PaginationItem key={index}>
                <PaginationEllipsis />
              </PaginationItem>
            );
          }

          return (
            <PaginationItem key={index}>
              <PaginationLink
                onClick={() => handlePageChange(pageNumber as number)}
                isActive={pageNumber === currentPage}
              >
                {pageNumber}
              </PaginationLink>
            </PaginationItem>
          );
        })}

        {currentPage !== lastPage && (
          <PaginationItem>
            <PaginationNext onClick={onNext} />
          </PaginationItem>
        )}
      </PaginationContent>
    </Pagination>
  );
};

export const CustomPageSelector = ({
  options = [10, 25, 30, 50],
  onPageSizeChange,
  pageSize,
  persistPagination = false,
  persistenceKey = "",
}: {
  options?: number[];
  onPageSizeChange: (value: number) => void;
  pageSize: number;
  persistPagination?: boolean;
  persistenceKey?: string;
}) => {
  const [storedPageSize, setStoredPageSize] = useSessionStorage({
    key: `${persistenceKey}_pageSize`,
    initialValue: pageSize,
    onError: (error: Error) => {
      console.error("Failed to handle page size storage:", error);
    },
  });

  React.useEffect(() => {
    if (persistPagination && persistenceKey && storedPageSize !== pageSize) {
      onPageSizeChange(storedPageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistPagination, persistenceKey, storedPageSize, pageSize]);

  const handlePageSizeChange = (value: number) => {
    onPageSizeChange(value);
    if (persistPagination && persistenceKey) {
      setStoredPageSize(value);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="my-2 border p-2 flex items-center justify-center gap-4 mt-4">
        <span>{pageSize}</span> <IoIosArrowDown />
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {options.map((item) => (
          <DropdownMenuItem
            key={item}
            onClick={() => handlePageSizeChange(item)}
          >
            {item}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
